import { Grid } from "@mui/material";
import { useState } from "react";
import { createListStore } from "../../common/listStore";
import { renderListStore } from "../../common/listStoreUi";
import { PageTitle } from "../../layout/PageTitle";
import { createEntityStore } from "../../common/entityStore";
import { combineLatest, distinctUntilChanged, map } from "rxjs";
import { unicodeName } from "unicode-name";
import { propTextField } from "../../common/entityStoreForm";
import { FormSection } from "../../common/FormSection";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { chunk, keyBy, sortBy, uniq, without } from "lodash";
import iconv from "iconv-lite";
import { useConfig } from "../../common/config";
import { chunkString, LandCode } from "../../common/defaultValues";
import { propValueValidator } from "../../common/entityStoreValidators";

interface AllowedCharactersDto {
    allowedCharacters: string;
    overrideCharacters: string;
    overrideCharactersInExport: string;
}

function getEncoding(landCode: LandCode) {
    switch (landCode) {
        case "CZ":
        case "SK":
        case "HU":
        case "HR":
        case "SI":
        case "PL":
            return "win1250";
        case "EE":
        case "LV":
            return "win1257";
        default:
            return "ascii";
    }
}

export function AllowedCharacterListPage() {
    const [useStore] = useState(() => createEntityStore<AllowedCharactersDto>({
        path: "settings/allowedCharacters",
        data: { allowedCharacters: "", overrideCharacters: "", overrideCharactersInExport: "" },
        onSave(data) {
            useStore.getState().setClientDataProp("allowedCharacters",
                sortBy(uniq(data.allowedCharacters.split(""))).join(""));
        },
        propValidators: [
            propValueValidator("overrideCharacters", p => p.length % 2 !== 0 && "Override characters must be in pairs"),
            propValueValidator("overrideCharacters", (p, e) => {
                const allowedCharsSet = new Set(e.allowedCharacters.split(""));
                return chunkString(p, 2).map(p => p[1]).some(c => !allowedCharsSet.has(c)) && `All override characters must be present in allowed characters.`;
            }),
            propValueValidator("overrideCharactersInExport", p => p.length % 2 !== 0 && "Override characters must be in pairs"),
            propValueValidator("overrideCharactersInExport", (p, e) => {
                const encoding = getEncoding(useConfig.getState().clientData.landCode);
                return getInvalidAnsiCharacters(encoding, e).filter(p => p.error != null).length !== 0 && `Table with not representable characters must be configured correctly.`;
            }),
        ],
    }));

    const [useInvalidAnsiListStore] = useState(() => createListStore<{
        error?: string;
        character: string; description: string; hex: string; ansi: string; ansiHex: string; ansiDescription: string;
        override?: string; overrideDescription?: string; overrideHex?: string; overrideAnsi?: string; overrideAnsiHex?: string; overrideAnsiDescription?: string;
    }>({
        data: combineLatest([
            useConfig.getState().clientData$.pipe(map(p => getEncoding(p.data.landCode)), distinctUntilChanged()),
            useStore.getState().clientData$,
        ]).pipe(map(([encoding, p]) => {
            return {
                items: getInvalidAnsiCharacters(encoding, p.data),
                isFetching: p.isFetching,
            };
        })),
        itemsPerPage: 15,
        getId: p => p.character,
        listProps: [
            { column: "character", label: "Character", width: 70 },
            { column: "hex", label: "Hex value", width: 70 },
            { column: "description", label: "Description", width: 250 },
            { column: "ansi", label: "ANSI representation", width: 70 },
            { column: "ansiHex", label: "ANSI hex values", width: 70 },
            { column: "ansiDescription", label: "ANSI description", width: 250 },
            { column: "override", label: "Override character", width: 70 },
            { column: "overrideHex", label: "Override hex value", width: 70 },
            { column: "overrideDescription", label: "Override description", width: 250 },
            { column: "overrideAnsi", label: "Override ANSI representation", width: 70 },
            { column: "overrideAnsiHex", label: "Override ANSI hex values", width: 70 },
            { column: "overrideAnsiDescription", label: "Override ANSI description", width: 250 },
            { column: "error", label: "Error", flex: 1 },
        ],
    }));

    const [useOverrideListStore] = useState(() => createListStore<{
        character: string;
        hex: string;
        description: string;
        override: string;
        overrideHex: string;
        overrideDescription: string;
    }>({
        data: useStore.getState().clientData$.pipe(map(p => ({
            items: chunk(p.data.overrideCharacters, 2).map(p => ({
                character: p[0],
                hex: p[0].charCodeAt(0).toString(16).toUpperCase().padStart(4, "0"),
                description: unicodeName(p[0]),
                override: p[1] ?? "",
                overrideHex: p[1] ? p[1].charCodeAt(0).toString(16).toUpperCase().padStart(4, "0") : "",
                overrideDescription: p[1] ? unicodeName(p[1]) : "",
            })),
            isFetching: p.isFetching,
        }))),
        itemsPerPage: 15,
        getId: p => p.character,
        listProps: [
            { column: "character", label: "Character", width: 80 },
            { column: "hex", label: "Hex value", width: 80 },
            { column: "description", label: "Description", width: 350 },
            { column: "override", label: "Override character", width: 80 },
            { column: "overrideHex", label: "Override hex value", width: 80 },
            { column: "overrideDescription", label: "Override description", width: 350 },
        ],
    }));
    const [useNewListStore] = useState(() => createListStore<{ character: string; description: string; hex: string; }>({
        data: combineLatest([useStore.getState().serverData$, useStore.getState().clientData$]).pipe(map(([s, c]) => ({
            items: without(sortBy(uniq(c.data.allowedCharacters)), ...s.data.allowedCharacters.split("")).map(p => ({
                character: p,
                description: unicodeName(p),
                hex: p.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0"),
            })),
            isFetching: s.isFetching || c.isFetching,
        }))),
        itemsPerPage: 15,
        getId: p => p.character,
        listProps: [
            { column: "character", label: "Character", width: 80 },
            { column: "hex", label: "Hexadecimal value", width: 80 },
            { column: "description", label: "Description", flex: 1, }
        ],
    }));
    const [useRemovedListStore] = useState(() => createListStore<{ character: string; description: string; hex: string; }>({
        data: combineLatest([useStore.getState().serverData$, useStore.getState().clientData$]).pipe(map(([s, c]) => ({
            items: without(sortBy(uniq(s.data.allowedCharacters)), ...c.data.allowedCharacters.split("")).map(p => ({
                character: p,
                description: unicodeName(p),
                hex: p.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0"),
            })),
            isFetching: s.isFetching || c.isFetching,
        }))),
        itemsPerPage: 15,
        getId: p => p.character,
        listProps: [
            { column: "character", label: "Character", width: 80 },
            { column: "hex", label: "Hexadecimal value", width: 80 },
            { column: "description", label: "Description", flex: 1, }
        ],
    }));
    const [useDbListStore] = useState(() => createListStore<{ character: string; description: string; hex: string; }>({
        data: useStore.getState().serverData$.pipe(map(p => ({
            items: uniq(sortBy(p.data.allowedCharacters)).map(p => ({
                character: p,
                description: unicodeName(p),
                hex: p.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0"),
            })),
            isFetching: p.isFetching,
        }))),
        itemsPerPage: 15,
        getId: p => p.character,
        listProps: [
            { column: "character", label: "Character", width: 80 },
            { column: "hex", label: "Hexadecimal value", width: 80 },
            { column: "description", label: "Description", flex: 1, }
        ],
    }));
    const hasOverrides = useOverrideListStore(p => p.items.length > 0);
    const hasInvalidAnsi = useInvalidAnsiListStore(p => p.items.length > 0);
    const hasNew = useNewListStore(p => p.items.length > 0);
    const hasRemoved = useRemovedListStore(p => p.items.length > 0);
    const landCode = useConfig(p => p.clientData.landCode);
    return <>
        <PageTitle title="Allowed characters" />
        <Grid container columnSpacing={2} alignItems="stretch" mb={8}>
            <Grid item xs={12}>{propTextField(useStore)("allowedCharacters", "All characters", { allowAllCharacters: true, sanitize: false })}</Grid>
            <Grid item xs={12}>{propTextField(useStore)("overrideCharacters", "Override characters (pairs)", { allowAllCharacters: true, sanitize: false })}</Grid>
            <Grid item xs={12}>{propTextField(useStore)("overrideCharactersInExport", "Override characters (pairs) for ANSI exports", { allowAllCharacters: true, sanitize: false })}</Grid>
            <Grid item xs={12}>
                <EntityStoreSaveButton useStore={useStore} />
            </Grid>
        </Grid>
        {hasOverrides && <FormSection label="Override characters">
            {renderListStore(useOverrideListStore, { autoHeight: true })}
        </FormSection>}
        {hasInvalidAnsi && <FormSection label={`Characters not representable in code page in ${landCode} (${getEncoding(landCode)})`}>
            {renderListStore(useInvalidAnsiListStore, { autoHeight: true, getRowClass: p => p.error ? ["redRibbon"] : ["greenRibbon"] })}
        </FormSection>}
        {hasNew && <FormSection label="New characters">
            {renderListStore(useNewListStore, { autoHeight: true, getRowClass: () => ["greenRibbon"] })}
        </FormSection>}
        {hasRemoved && <FormSection label="Removed characters">
            {renderListStore(useRemovedListStore, { autoHeight: true, getRowClass: () => ["redRibbon"] })}
        </FormSection>}
        <FormSection label="Current allowed characters in database">
            {renderListStore(useDbListStore, { autoHeight: true })}
        </FormSection>
    </>;
}

function ansiHex(ansi: Buffer) {
    return ansi.toJSON().data.map((p: number) => p.toString(16).toUpperCase().padStart(2, "0")).join(" ");
}

function hex(p: string) {
    return p.split("").map(c => c.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0")).join(" ")
}

function getInvalidAnsiCharacters(encoding: string, p: AllowedCharactersDto) {
    const overridesDict = keyBy(chunk(p.overrideCharactersInExport.split(""), 2).map(p => [p[0], p[1] ?? ""]), p => p[0]);
    return uniq(sortBy(p.allowedCharacters).concat(Object.keys(overridesDict))).map(char => {
        const ansi = iconv.encode(char, encoding);
        const decoded = iconv.decode(ansi, encoding);
        const override = overridesDict[char]?.at(1) || undefined;
        const overrideAnsi = override != null ? iconv.encode(override, encoding) : null;
        const overrideDecoded = overrideAnsi != null ? iconv.decode(overrideAnsi, encoding) : null;
        return {
            data: {
                character: char,
                description: unicodeName(char),
                hex: hex(char),
                ansi: decoded,
                ansiHex: ansiHex(ansi),
                ansiDescription: unicodeName(decoded),
                override,
                overrideDescription: override != null ? unicodeName(override) : undefined,
                overrideHex: override != null ? hex(override) : undefined,
                overrideAnsi: overrideDecoded ?? undefined,
                overrideAnsiHex: overrideAnsi != null ? ansiHex(overrideAnsi) : undefined,
                overrideAnsiDescription: overrideDecoded != null ? unicodeName(overrideDecoded) : undefined,
                error:
                    decoded !== char && override == null ? "Character is missing override." :
                        override != null && override !== overrideDecoded ? "Override character is not representable in ANSI." :
                            undefined,
            },
            display: char !== decoded || (char === decoded && overridesDict[char] != null),
        };
    }).filter(p => p.display).map(p => p.data);
}
